/**
 * iter86.43 — Matchday Boost panel + data hook.
 *
 * The weekly engagement loop for soccer seasons: pick up to 2 MATCH
 * WINNERS from the current matchday's fixtures (any club, any pot).
 * Payout is tiered by the picked club's pot: win 3/5/7 · draw 1/2/3
 * (Pot 1 / Pot 2 / Pot 3+). Each pick is changeable until ITS fixture
 * kicks off; a started pick is consumed and counts toward the 2.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronUp, Clock, Flag, Info, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { nationalFlag } from "@/lib/flags";
import { isTournamentComplete, TOURNAMENT_COMPLETE_LOCK_COPY } from "@/lib/todoStatus";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

export function useMatchdayBoost(tournament, meId) {
    const enabled = !!tournament?.matchday_boost?.enabled;
    const [state, setState] = useState(null);
    // Stale-response guard: meId resolves after mount, so an early
    // no-participant fetch can land LAST and wipe my_picks.
    const reqIdRef = useRef(0);
    const load = useCallback(async () => {
        if (!enabled || !tournament?.id) return;
        const rid = ++reqIdRef.current;
        try {
            const data = await api.getMatchdayBoostState(tournament.id, meId);
            if (rid === reqIdRef.current) setState(data);
        } catch (e) { /* boost state is best-effort */ }
    }, [enabled, tournament?.id, meId]);
    useEffect(() => { load(); }, [load]);
    const attention = useMemo(() => {
        if (!enabled || !state || !meId) return false;
        const open = (state.fixtures || []).some(f => !f.started);
        return open && (state.my_picks || []).length < (state.picks_per_round || 2);
    }, [enabled, state, meId]);
    return { enabled, state, refresh: load, attention };
}

const POT_TONE = { 1: "text-zinc-400", 2: "text-cyber", 3: "text-volt" };

// iter86.44 — week/matchweek selector, bounded by the season window's rounds.
// iter87.115 — American football (NFL/CFB spread boost) uses "Week" +
// "Week 1"; soccer keeps "Matchweek" + "MD 1".
function RoundSelect({ rounds, value, currentRound, onChange, spreadMode }) {
    if (!rounds || rounds.length <= 1) return null;
    const outerLabel = spreadMode ? "Week" : "Matchweek";
    const optionPrefix = spreadMode ? "Week" : "MD";
    return (
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
            {outerLabel}
            <select
                data-testid="matchday-boost-round-select"
                value={value ?? ""}
                onChange={(e) => onChange(Number(e.target.value))}
                className="bg-ink border-2 border-[#333] rounded-md px-2 py-1 text-xs font-mono font-bold text-white hover:border-volt focus:border-volt focus:outline-none"
            >
                {rounds.map(r => (
                    <option key={r} value={r}>
                        {optionPrefix} {r}{r === currentRound ? " · current" : ""}
                    </option>
                ))}
            </select>
        </label>
    );
}

// iter87.5 — split date/time so the middle column can stack them
// vertically on narrow phones (iPhone 16 in portrait) without
// truncating team names on either side.
function kickoffDate(iso) {
    if (!iso) return "TBD";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "short", month: "numeric", day: "numeric",
        });
    } catch { return ""; }
}
function kickoffTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "numeric", minute: "2-digit",
        });
    } catch { return ""; }
}

export default function MatchdayBoostPanel({ tournament, me, state, onRefresh, footerSlot }) {
    // iter87.244 (user-reported) — "match"/"matchday" is soccer
    // terminology; every football template (NFL Season sidecar, NFL
    // Weekly Spread Boost, NFL/CFB Playoffs Weekly Spread — all of which
    // render through this same panel) should read "game"/"week" instead.
    // Derived from the template (not `spreadMode`, which only exists
    // once `state` has loaded) so it's available for the very first
    // "Loading…" render too.
    const footballTpl = tournament?.template;
    const isFootball = footballTpl === "nfl_season_api" || footballTpl === "nfl_boost_api"
        || isFootballPlayoffsTemplate(footballTpl);
    // iter87.564 (user-requested — "delete dead branch") — playoff
    // templates (NFL/CFB/MLS Playoffs) never actually render this
    // component: `MatchdayBoostModal.jsx` routes them to
    // `PlayoffPredictorPanel.jsx` instead (iter87.467). Make any
    // playoff-predictor UI change there, not here.
    const [picks, setPicks] = useState(null); // null = derive from saved
    const [saving, setSaving] = useState(false);
    const [showLedger, setShowLedger] = useState(false);
    // iter86.44 — browse/pick future matchdays. null = current matchday.
    const [selectedRound, setSelectedRound] = useState(null);
    const [altState, setAltState] = useState(null);
    const currentRound = state?.current_round;
    const viewingCurrent = selectedRound == null || selectedRound === currentRound;
    useEffect(() => {
        if (!state || viewingCurrent) { setAltState(null); return; }
        let alive = true;
        api.getMatchdayBoostState(tournament.id, me?.id, selectedRound)
            .then(d => { if (alive) setAltState(d); })
            .catch(() => {});
        return () => { alive = false; };
    }, [state, viewingCurrent, selectedRound, tournament?.id, me?.id]);
    if (!state) return <p className="text-sm text-zinc-500 py-6 text-center">{isFootball ? "Loading week…" : "Loading matchday…"}</p>;

    // iter87.199.161 — Weekly Spread mid-season start gate. Same
    // component (and same "waiting" copy) whether reached via the
    // dashboard tab or the top-level persistent banner.
    if (state.pending_start) {
        return (
            <div className="brutal-card p-5 text-center space-y-2" data-testid="matchday-boost-pending-start">
                <Flag className="w-6 h-6 text-volt mx-auto" />
                <p className="font-bold text-sm">This league hasn't started yet</p>
                <p className="text-xs text-zinc-500 max-w-sm mx-auto">
                    {me?.is_admin || me?.is_co_admin
                        ? `Invite your crew, then hit "Start Tournament" (top of the dashboard) whenever you're ready — picks will open from Week ${state.start_week_candidate ?? "—"}.`
                        : `Waiting on your admin to start the league — picks will open from Week ${state.start_week_candidate ?? "—"}.`}
                </p>
            </div>
        );
    }

    const view = viewingCurrent ? state : altState;
    const round = viewingCurrent ? currentRound : selectedRound;
    const futureRounds = (state.rounds || []).filter(r => currentRound == null || r >= currentRound);
    const changeRound = (r) => { setSelectedRound(r === currentRound ? null : r); setPicks(null); };
    // iter87.124 — Hoist spreadMode above the early-return so the
    // RoundSelect inside the "Loading week N…" fallback can pick the
    // right label ("Week" vs "Matchweek"). Prior to this, referencing
    // `spreadMode` inside the `if (!view)` branch triggered a
    // temporal-dead-zone crash ("Cannot access 'spreadMode' before
    // initialization") when a user selected a future week whose
    // altState hadn't finished loading yet.
    const spreadMode = (view || state)?.scoring_mode === "spread";

    if (!view) {
        return (
            <div className="brutal-card p-5 space-y-4" data-testid="matchday-boost-panel">
                <RoundSelect rounds={futureRounds} value={round} currentRound={currentRound} onChange={changeRound} spreadMode={spreadMode} />
                <p className="text-sm text-zinc-500 py-6 text-center">{isFootball ? `Loading week ${round}…` : `Loading matchday ${round}…`}</p>
            </div>
        );
    }

    const cap = view.picks_per_round || 2;
    const payout = view.payout || {};
    const fmtSpread = (v) => (v == null ? null : v > 0 ? `+${v}` : `${v}`);
    const saved = view.my_picks || [];
    const effective = picks ?? saved;
    const byFixture = Object.fromEntries(effective.map(p => [p.fixture_id, p.team_id]));
    // iter87.29 — Keep the FULL saved pick so we can show the captured
    // line ("Locked at KC −3.5") on the picked side. Only saved picks
    // carry `captured_spread_home`; drafts (`picks` state) don't.
    const savedByFixture = Object.fromEntries(saved.map(p => [p.fixture_id, p]));
    const fixtures = view.fixtures || [];
    const openCount = fixtures.filter(f => !f.started).length;
    const consumed = effective.filter(p => {
        const f = fixtures.find(x => x.fixture_id === p.fixture_id);
        return f && f.started;
    });
    const dirty = JSON.stringify([...effective].sort((a, b) => a.fixture_id.localeCompare(b.fixture_id)))
        !== JSON.stringify([...saved].sort((a, b) => a.fixture_id.localeCompare(b.fixture_id)));
    const ledger = (state.ledger || []).filter(l => currentRound == null || l.round < currentRound);
    const totalBanked = (state.ledger || []).reduce((n, l) => n + (l.points || 0), 0);

    const toggle = (fixture, side) => {
        if (!me) return;
        if (fixture.started) {
            toast.error(isFootball ? "That game already kicked off" : "That match already kicked off");
            return;
        }
        const cur = byFixture[fixture.fixture_id];
        let next;
        if (cur === side.team_id) {
            next = effective.filter(p => p.fixture_id !== fixture.fixture_id);
        } else if (cur) {
            // iter87.370 — Switching sides on an already-picked game
            // keeps its Over/Under sub-pick intact.
            next = effective.map(p => p.fixture_id === fixture.fixture_id ? { fixture_id: fixture.fixture_id, team_id: side.team_id, ou_pick: p.ou_pick || null } : p);
        } else {
            if (effective.length >= cap) {
                toast.error(`Max ${cap} boost picks this ${isFootball ? "week" : "matchday"} — deselect one first`);
                return;
            }
            next = [...effective, { fixture_id: fixture.fixture_id, team_id: side.team_id, ou_pick: null }];
        }
        setPicks(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.submitMatchdayBoostPicks(tournament.id, me.id, round, effective);
            toast.success(spreadMode ? `Week ${round} spread picks locked in ⚡` : `Matchday ${round} boosts locked in ⚡`);
            setPicks(null);
            onRefresh?.();
        } catch (err) {
            const detail = err.response?.data?.detail || "Could not save boost picks";
            // iter87.199.80 — When the backend rejects a save because
            // the admin lowered `picks_per_round` mid-week, the toast
            // includes the effective cap. Refresh the live state so
            // the modal snaps to the new cap; if the local selection
            // exceeds it, trim from the tail so the user's next save
            // succeeds without them having to hunt for the extras.
            const capMatch = typeof detail === "string" && detail.match(/^Max (\d+) boost picks/);
            if (capMatch) {
                const newCap = parseInt(capMatch[1], 10);
                if (Number.isInteger(newCap) && effective.length > newCap) {
                    const trimmed = effective.slice(0, newCap);
                    setPicks(trimmed);
                    toast.error(`Picks per week was lowered to ${newCap}. Trimmed your selection — review and save again.`);
                    onRefresh?.();
                    return;
                }
            }
            toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save boost picks"));
            // Always refresh so a stale cap shown in the header snaps
            // to whatever the admin most recently set.
            onRefresh?.();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="matchday-boost-panel">
            <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                    {/* iter87.379 (user-requested) — removed the
                        duplicate "⚡ Playoff Predictor · round" title
                        here; `MatchdayBoostModal.jsx`'s own header
                        already announces the feature name + lock
                        status right above this panel. */}
                    {!viewingCurrent && (
                        <span className="pill bg-cyber/15 border-cyber text-cyber text-[9px] font-bold mb-2 inline-block" data-testid="matchday-boost-ahead-badge">picking ahead</span>
                    )}
                    {/* iter87.465.26 (user-requested — "put that [How
                        it works:] in front of all the sub heading
                        copy for all the pick something modals in pre
                        / in season") — Info icon + bold label,
                        matching the Series Predictor modals
                        (MlsSeriesPickModal.jsx etc). */}
                    <div data-testid="matchday-boost-how-it-works" className="text-zinc-500 text-[11px] leading-relaxed max-w-xl flex gap-2 items-start text-left">
                        <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                            <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                            {spreadMode ? (
                                <>
                                    Pick <b className="text-volt">{cap} teams</b> this week — graded against the line frozen at YOUR pick time:
                                    {" "}<span className="text-volt font-bold">win + cover +{payout.win_cover ?? 6}</span> ·
                                    {" "}<span className="text-cyber font-bold">win +{payout.win ?? 2}</span> ·
                                    {" "}<span className="text-zinc-400 font-bold">loss that covers {payout.loss_cover ?? 0}</span> ·
                                    {" "}<span className="text-heat font-bold">miss both {payout.loss ?? -2}</span>.
                                    Line movement after you lock in doesn't affect your grade. Each pick locks at its game's kickoff. <span className="text-zinc-500 italic">Ties count as losses & pushes against the spread are losses as well — very American 🇺🇸.</span>
                                </>
                            ) : (
                                <>
                                    Pick up to <b className="text-volt">{cap} match winners</b> this matchday — any club, any pot.
                                    Deeper pots pay more: <span className={POT_TONE[1]}>Pot 1 · W+3 / D+1</span> · <span className={POT_TONE[2]}>Pot 2 · W+5 / D+2</span> · <span className={POT_TONE[3]}>Pot 3+ · W+7 / D+3</span>.
                                    Each pick locks when its match kicks off. Skip a week? No harm done.
                                </>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full justify-end lg:w-auto">
                    <RoundSelect rounds={futureRounds} value={round} currentRound={currentRound} onChange={changeRound} spreadMode={spreadMode} />
                    <span className="pill bg-volt/15 border-volt text-volt font-bold" data-testid="matchday-boost-count">
                        {`${effective.length}/${cap} picked`}
                    </span>
                    {totalBanked > 0 && (
                        <span className="text-[10px] text-zinc-500 font-mono w-full sm:w-auto text-right" data-testid="matchday-boost-banked">banked so far: +{totalBanked}</span>
                    )}
                </div>
            </div>

            {consumed.length >= cap && openCount > 0 && (
                <p className="text-[11px] text-blaze font-bold" data-testid="matchday-boost-consumed-note">
                    <Lock className="w-3 h-3 inline mr-1" />
                    {isTournamentComplete(tournament)
                        ? TOURNAMENT_COMPLETE_LOCK_COPY
                        : isFootball
                            ? "Both picks are locked in — their games kicked off. See you next week!"
                            : "Both picks are locked in — their matches kicked off. See you next matchday!"}
                </p>
            )}

            <div className="space-y-1.5" data-testid="matchday-boost-fixtures">
                {fixtures.length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4">No fixtures on the schedule yet.</p>
                )}
                {fixtures.map(f => {
                    const pickedTeam = byFixture[f.fixture_id];
                    const savedPick = savedByFixture[f.fixture_id];
                    // iter87.29 — captured line label for the picked
                    // side. Priority: saved pick's captured line →
                    // current live/frozen game line → pending.
                    const capturedLabel = (side) => {
                        if (!spreadMode) return null;
                        const isPicked = pickedTeam === side.team_id;
                        const isSavedSameTeam = isPicked && savedPick?.team_id === side.team_id;
                        // Homeref line → the picker sees THEIR side.
                        const homeRef = (line) => side.team_id === f.home?.team_id ? line : -line;
                        if (isSavedSameTeam && savedPick?.captured_spread_home != null) {
                            const src = savedPick.captured_source;
                            const line = fmtSpread(homeRef(savedPick.captured_spread_home));
                            if (src === "frozen") return `Locked at ${line}`;
                            if (src === "backfill_first") return `First line ${line}`;
                            if (src === "carried") return `Locked at ${line}`;
                            return `Locked at ${line}`; // "live" or default
                        }
                        if (isSavedSameTeam && savedPick?.captured_source === "pending") {
                            return "Awaiting first line";
                        }
                        if (f.spread_home != null) return `Line ${fmtSpread(homeRef(f.spread_home))}`;
                        if (f.spread_home_live != null) return `~${fmtSpread(homeRef(f.spread_home_live))} early`;
                        return "Line TBD";
                    };
                    const sideBtn = (side) => {
                        const isPicked = pickedTeam === side.team_id;
                        return (
                            <button
                                type="button"
                                data-testid={`boost-side-${f.fixture_id}-${side.team_id}`}
                                disabled={!me || (f.started && !isPicked)}
                                onClick={() => toggle(f, side)}
                                className={`min-w-0 w-full p-2 sm:p-3 min-h-[56px] border rounded-lg text-center transition-colors active:scale-[0.98] ${
                                    isPicked
                                        ? "border-volt bg-volt/15"
                                        : f.started
                                            ? "border-white/[0.04] bg-white/[0.02] opacity-50 cursor-not-allowed"
                                            : "border-white/[0.08] bg-[#1A1A1A] hover:border-volt"
                                }`}
                            >
                                <span className="text-[13px] sm:text-sm font-bold leading-tight block break-words">
                                    {nationalFlag(side.flag) ? `${nationalFlag(side.flag)} ` : ""}{side.name}
                                    {isPicked && <Check className="w-3 h-3 inline ml-1 text-volt" />}
                                </span>
                                <span
                                    className={`text-[9px] uppercase tracking-wider block ${spreadMode ? (isPicked && savedPick?.captured_spread_home != null ? "text-volt font-bold" : "text-zinc-500") : (POT_TONE[side.pot] || "text-zinc-500")}`}
                                    data-testid={`boost-side-line-${f.fixture_id}-${side.team_id}`}
                                >
                                    {spreadMode
                                        ? capturedLabel(side)
                                        : `Pot ${side.pot} · W+${side.win_points} / D+${side.draw_points}`}
                                </span>
                            </button>
                        );
                    };
                    return (
                        <div key={f.fixture_id} className="space-y-1" data-testid={`boost-fixture-${f.fixture_id}`}>
                            <div
                                className="grid items-center gap-1.5 sm:gap-2"
                                style={{ gridTemplateColumns: "minmax(0,1fr) 64px minmax(0,1fr)" }}
                            >
                                {sideBtn(f.home)}
                                <div className="text-center leading-tight">
                                    {f.played ? (
                                        <span className="font-mono font-bold text-sm">{f.score_a}–{f.score_b}</span>
                                    ) : f.started ? (
                                        <span className="text-[9px] uppercase tracking-wider text-heat">LIVE</span>
                                    ) : (
                                        <span className="text-[9px] uppercase tracking-wider text-zinc-500 flex flex-col items-center gap-0.5">
                                            <Clock className="w-2.5 h-2.5" />
                                            <span>{kickoffDate(f.scheduled_at)}</span>
                                            {/* iter87.125 — When the vendor stamped a
                                                placeholder kickoff (00:00 UTC), show
                                                "Time TBD" instead of a bogus time that
                                                rolls back to Friday in US Eastern. The
                                                scheduled_at was already anchored to
                                                Saturday 17:00 UTC by the parser so the
                                                date above is correct. */}
                                            <span>{f.time_tbd ? "Time TBD" : kickoffTime(f.scheduled_at)}</span>
                                        </span>
                                    )}
                                </div>
                                {sideBtn(f.away)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* iter87.380 (user-requested) — the save bar now renders
                into the modal's floating footer slot (flush against
                the modal's bottom border, matching the Champion pick
                modals) instead of scrolling inline with the picks.
                Still appears only when there are unsaved changes. */}
            {footerSlot && !!me && dirty && createPortal(
                <div className="glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="boost-pick-slip">
                    <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="font-display font-black text-sm uppercase tracking-tight leading-tight">
                                <span className={effective.length >= cap ? "text-neon" : "text-volt"} data-testid="boost-pick-slip-count">
                                    {effective.length}/{cap}
                                </span>{" "}
                                {spreadMode ? "spread picks" : "boost picks"}
                            </p>
                            {effective.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2 max-h-20 overflow-y-auto" data-testid="boost-pick-slip-chips">
                                    {effective.map((p) => {
                                        const f = fixtures.find((x) => x.fixture_id === p.fixture_id);
                                        const side = [f?.home, f?.away].find((s) => s?.team_id === p.team_id);
                                        const label = side?.name || "Pick";
                                        return (
                                            <span
                                                key={p.fixture_id}
                                                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] px-2 py-1 text-[10px] font-bold uppercase tracking-wide"
                                            >
                                                {label}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${label}`}
                                                    data-testid={`boost-pick-slip-chip-remove-${p.fixture_id}`}
                                                    onClick={() => setPicks(effective.filter((pk) => pk.fixture_id !== p.fixture_id))}
                                                    className="text-zinc-400 hover:text-heat transition-colors"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="matchday-boost-save-btn"
                            onClick={save}
                            disabled={saving}
                            className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-volt text-white font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                        >
                            {saving ? "Saving…" : (spreadMode ? `Save Week ${round}` : `Save MD ${round}`)}
                        </button>
                    </div>
                </div>,
                footerSlot
            )}

            {ledger.length > 0 && (
                <div className="border-t-2 border-[#222] pt-3">
                    <button
                        type="button"
                        data-testid="matchday-boost-ledger-toggle"
                        onClick={() => setShowLedger(v => !v)}
                        className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white flex items-center gap-1"
                    >
                        {showLedger ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {isFootball ? `Past weeks (${ledger.length})` : `Past matchdays (${ledger.length})`}
                    </button>
                    {showLedger && (
                        <div className="space-y-1 mt-2" data-testid="matchday-boost-ledger">
                            {[...ledger].reverse().map(l => (
                                <div key={l.round} className="flex items-center justify-between text-xs">
                                    <span className="text-zinc-500 font-mono w-14">{isFootball ? `Wk ${l.round}` : `MD ${l.round}`}</span>
                                    <span className="flex-1 truncate text-zinc-400">
                                        {(l.picks || []).map(pk => (
                                            <span key={pk.fixture_id} className="mr-2">
                                                {nationalFlag(pk.flag)} {pk.team_name} {pk.won ? "✅" : pk.drew ? "➖" : pk.played ? "❌" : "⏳"}
                                            </span>
                                        ))}
                                        {(l.picks || []).length === 0 && <span className="italic text-zinc-600">skipped</span>}
                                    </span>
                                    <span className={`font-mono font-bold ${l.points > 0 ? "text-volt" : "text-zinc-600"}`}>+{l.points}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
